import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import emailjs from "emailjs-com";
import {
  FaArrowRight,
  FaBullhorn,
  FaCheckCircle,
  FaEnvelope,
  FaExclamationCircle,
  FaPaperPlane,
  FaPhoneAlt,
  FaStore,
  FaUsers,
} from "react-icons/fa";
import Header from "./Header";
import Footer from "./Footer";
import "./NeedHelf.css";

const SUBJECT_MAX = 80;
const MESSAGE_MAX = 1000;

const CONTACT_EMAIL1 = "goourhub@gmail.com";
const CONTACT_EMAIL2 = "contact@goourhub.com";
const CONTACT_PHONE = "210-954-1284";
const CONTACT_PHONE_LINK = "+12109541284";

const EMAILJS_SERVICE_ID = "service_wcb9p0d";
const EMAILJS_TEMPLATE_ID = "template_xt8g4fq";
const EMAILJS_PUBLIC_KEY = "4L6c5XHTR-ND4i4-d";

const HELP_PATHS = [
  {
    key: "hub",
    eyebrow: "Discovery",
   title: "Explore communities (Use Hub)",

    icon: <FaUsers />,
    intro:
      "Go to the Hub when you want to explore communities, business categories, guides, and local discovery pages.",
    steps: [
      "Open the Hub.",
      "Choose a community or browse a category like Restaurants, Groceries, or Services.",
      "Open the page that matches what you want.",
    ],
    examples: [
      "I want Ethiopian restaurants → Open Hub → choose Ethiopian or Restaurants.",
      "I want local businesses by community → Open Hub → choose the community page.",
    ],
    ctaTo: "/hub",
    ctaLabel: "Open Hub",
  },
  {
    key: "hubmart",
    eyebrow: "Marketplace",
   title: "Buy or sell locally (HubMart)",

    icon: <FaStore />,
    intro:
      "Go to HubMart when you want to buy, sell, donate, browse listings, or post something for San Antonio.",
    steps: [
      "Open HubMart.",
      "Pick a category or browse listings.",
      "Use filters, sort, or post your own listing.",
    ],
    examples: [
      "I want to buy a used phone → Open HubMart → Mobile.",
      "I want to post a couch for sale → Open HubMart → Post listing.",
    ],  
    ctaTo: "/HubMart",
    ctaLabel: "Open HubMart",
  },
  {
    key: "partner",
    eyebrow: "Visibility",
    title: "Promote your business (Partner)",
    icon: <FaBullhorn />,
    intro:
      "Use this path when you want your business, event, or community featured or you want to talk partnership.",
    steps: [
      "Open the partner page.",
      "Share what you want promoted or supported.",
      "If anything is unclear, send a message below.",
    ],
    examples: [
      "I want my business featured → Partner with us.",
      "I want to talk about community visibility → Partner with us or message us below.",
    ],
    ctaTo: "/partner-signup",
    ctaLabel: "Partner with us",
  },
];

function NeedHelp() {
  const [openHelp, setOpenHelp] = useState("hub");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState({ type: "idle", text: "" });

  const subjectCharsLeft = SUBJECT_MAX - subject.length;
  const messageCharsLeft = MESSAGE_MAX - message.length;
  const helperTone = messageCharsLeft < 120 ? "is-warning" : "";

  useEffect(() => {
    try {
      if (typeof emailjs.init === "function") {
        emailjs.init(EMAILJS_PUBLIC_KEY);
      }
    } catch (error) {
      console.error("EmailJS init error:", error);
    }
  }, []);

  const resetForm = () => {
    setName("");
    setEmail("");
    setSubject("");
    setMessage("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!name.trim() || !email.trim() || !subject.trim() || !message.trim()) {
      setStatus({
        type: "error",
        text: "Please fill in all required fields before sending.",
      });
      return;
    }

    setIsSubmitting(true);
    setStatus({ type: "idle", text: "" });

    try {
      const response = await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        {
          from_name: name.trim(),
          reply_to: email.trim(),
          email: email.trim(),
          subject: subject.trim(),
          message: message.trim(),
        },
        EMAILJS_PUBLIC_KEY
      );

      console.log("EmailJS success:", response);
      resetForm();

      setStatus({
        type: "success",
        text: "Your message has been sent. We’ll get back to you soon.",
      });
    } catch (error) {
      console.error("EmailJS error:", error);
      setStatus({
        type: "error",
        text: error?.text || "Something went wrong while sending your message.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact-page-shell">
      <Header />

      <main className="contact-page">
        <section className="contact-help-guide">
          <div className="contact-help-guide__head">
            <span className="contact-kicker">Need help?</span>
            <h1>Choose the right path first.</h1>
            <p>
              This page should answer the “where do I go now?” question before
              asking anyone to type. Start with Hub, HubMart, or partnership.
              If you still need help, use the contact form below.
            </p>
          </div>

         <div className="contact-help-grid">
  {HELP_PATHS.map((item) => (
    <article
      key={item.key}
      className={`contact-help-item contact-help-item--${item.key} is-static`}
    >
      <div className="contact-help-summary static">
        <span className="contact-help-summary__icon">{item.icon}</span>

        <div className="contact-help-summary__copy">
          <span className="contact-help-summary__eyebrow">
            {item.eyebrow}
          </span>
          <strong>{item.title}</strong>
        </div>
      </div>

      <div className="contact-help-body always-open">
        <p className="contact-help-body__intro">{item.intro}</p>

        <div className="contact-help-route">
          <span className="contact-help-route__label">Best route</span>
          <ol className="contact-help-route__steps">
            {item.steps.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ol>
        </div>

        <div className="contact-help-example">
          <span className="contact-help-route__label">Example</span>
          <p>{item.examples[0]}</p>
        </div>

        <Link to={item.ctaTo} className="contact-help-btn">
          {item.ctaLabel}
          <FaArrowRight />
        </Link>
      </div>
    </article>
  ))}
</div>

          <div className="contact-help-strip">
            <a className="contact-help-inline" href={`mailto:${CONTACT_EMAIL1}`}>
              <span className="contact-help-inline__icon">
                <FaEnvelope />
              </span>
              <span className="contact-help-inline__copy">
                <span>Email us</span>
                <strong>{CONTACT_EMAIL1}</strong>
              </span>
            </a>

            <a className="contact-help-inline" href={`tel:${CONTACT_PHONE_LINK}`}>
              <span className="contact-help-inline__icon">
                <FaPhoneAlt />
              </span>
              <span className="contact-help-inline__copy">
                <span>Call us</span>
                <strong>{CONTACT_PHONE}</strong>
              </span>
            </a>

            <div className="contact-help-inline contact-help-inline--soft">
              <span className="contact-help-inline__icon">
                <FaPaperPlane />
              </span>
              <span className="contact-help-inline__copy">
                <span>Still unsure?</span>
                <strong>Scroll down and send us a message.</strong>
              </span>
            </div>
          </div>
        </section>

        <section className="contact-hero-card">
          <div className="contact-hero-copy">
            <span className="contact-kicker">Contact GoOurHub</span>
            <h2 className="contact-hero-copy__title">
              Still need support, partnerships, or your next move?
            </h2>
            <p>
              Whether you need help with the Hub, HubMart, business visibility,
              or a partnership conversation, send a clear message and we’ll take
              it from there.
            </p>

            <div className="contact-direct-card">
              <div className="contact-direct-item">
                <span className="contact-direct-item__icon">
                  <FaEnvelope />
                </span>

                <div>
                  <span className="contact-direct-label">Email us</span>
                  <div className="contact-direct-links">
                    <a
                      className="contact-direct-link"
                      href={`mailto:${CONTACT_EMAIL1}`}
                    >
                      {CONTACT_EMAIL1}
                    </a>
                    <a
                      className="contact-direct-link"
                      href={`mailto:${CONTACT_EMAIL2}`}
                    >
                      {CONTACT_EMAIL2}
                    </a>
                  </div>
                </div>
              </div>

              <div className="contact-direct-item">
                <span className="contact-direct-item__icon">
                  <FaPhoneAlt />
                </span>

                <div>
                  <span className="contact-direct-label">Call us</span>
                  <a
                    className="contact-direct-link"
                    href={`tel:${CONTACT_PHONE_LINK}`}
                  >
                    {CONTACT_PHONE}
                  </a>
                </div>
              </div>
            </div>

            <div className="contact-points">
              <div className="contact-point">
                <span className="contact-point__icon">
                  <FaEnvelope />
                </span>
                <div>
                  <strong>Direct contact</strong>
                  <p>
                    Use the form below for questions, support, or business
                    inquiries.
                  </p>
                </div>
              </div>

              <div className="contact-point">
                <span className="contact-point__icon">
                  <FaPaperPlane />
                </span>
                <div>
                  <strong>Clearer messages get faster replies</strong>
                  <p>
                    Add the reason for reaching out, what page you were on, and
                    anything relevant.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="contact-card">
            <div className="contact-card__header">
              <div>
                <span className="contact-card__eyebrow">Send a message</span>
                <h2>We’re listening</h2>
              </div>
              <div className="contact-card__meta">Direct inbox delivery</div>
            </div>

            {status.type !== "idle" && (
              <div
                className={`contact-status contact-status--${status.type}`}
                role="status"
              >
                <span className="contact-status__icon">
                  {status.type === "success" ? (
                    <FaCheckCircle />
                  ) : (
                    <FaExclamationCircle />
                  )}
                </span>
                <span>{status.text}</span>
              </div>
            )}

            <form className="contact-form" onSubmit={handleSubmit} noValidate>
              <div className="contact-grid">
                <div className="contact-field">
                  <label htmlFor="contact-name">Name</label>
                  <input
                    id="contact-name"
                    type="text"
                    name="from_name"
                    placeholder="Your name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                  />
                </div>

                <div className="contact-field">
                  <label htmlFor="contact-email">Email</label>
                  <input
                    id="contact-email"
                    type="email"
                    name="reply_to"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="contact-field">
                <div className="contact-field__topline">
                  <label htmlFor="contact-subject">Subject</label>
                  <span className="contact-counter">{subjectCharsLeft} left</span>
                </div>
                <input
                  id="contact-subject"
                  type="text"
                  name="subject"
                  placeholder="What do you need help with?"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  maxLength={SUBJECT_MAX}
                  required
                />
              </div>

              <div className="contact-field">
                <div className="contact-field__topline">
                  <label htmlFor="contact-message">Message</label>
                  <span className={`contact-counter ${helperTone}`}>
                    {messageCharsLeft} left
                  </span>
                </div>
                <textarea
                  id="contact-message"
                  name="message"
                  placeholder="Tell us what’s going on, what page you were on, and what outcome you need."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  maxLength={MESSAGE_MAX}
                  required
                  rows={7}
                />
              </div>

              <button
                type="submit"
                className="contact-submit-btn"
                disabled={isSubmitting}
              >
                <FaPaperPlane />
                <span>{isSubmitting ? "Sending..." : "Send message"}</span>
              </button>
            </form>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

export default NeedHelp;